import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Wallet, Store, User, DollarSign, Users, Calculator, 
  ExternalLink, Settings2, Building2, Globe, IndianRupee, 
  CreditCard as CardIcon, Lock, AlertCircle, Mail, Send, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface GroupPaymentConfigProps {
  entityType: 'purchase' | 'event';
  entityId: string;
  currentConfig: {
    payment_enabled?: boolean;
    payment_type: 'none' | 'merchant' | 'organizer';
    split_type: 'equal' | 'custom' | 'open';
    target_amount: number | null;
    collected_amount: number;
    currency: string;
    payment_link?: string | null;
    organizer_upi_id?: string | null;
    merchant_id?: string | null;
    razorpay_linked_account_id?: string | null;
    stripe_connected_account_id?: string | null;
    payment_provider?: 'razorpay' | 'stripe' | null;
    platform_fee_per_participant?: number | null;
  };
  participantCount: number;
  isOrganizer: boolean;
  onConfigUpdated: () => void;
  organizerEmail?: string;
  // Group Payment specific - pending invitations that haven't been sent yet
  pendingInvitationsCount?: number;
  purchaseTitle?: string;
  organizerName?: string;
}

const CURRENCIES = [
  { code: 'INR', symbol: '₹', label: 'Indian Rupee' },
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
];

const COUNTRIES = [
  { code: "IN", name: "India", currency: "INR", provider: "razorpay" as const },
  { code: "US", name: "United States", currency: "USD", provider: "stripe" as const },
  { code: "GB", name: "United Kingdom", currency: "GBP", provider: "stripe" as const },
  { code: "CA", name: "Canada", currency: "CAD", provider: "stripe" as const },
  { code: "AU", name: "Australia", currency: "AUD", provider: "stripe" as const },
  { code: "AE", name: "UAE", currency: "AED", provider: "stripe" as const },
];

export function GroupPaymentConfig({
  entityType,
  entityId,
  currentConfig,
  participantCount,
  isOrganizer,
  onConfigUpdated,
  organizerEmail,
  pendingInvitationsCount = 0,
  purchaseTitle,
  organizerName,
}: GroupPaymentConfigProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [paymentType, setPaymentType] = useState<'merchant' | 'organizer'>(
    currentConfig.payment_type === 'none' ? 'organizer' : currentConfig.payment_type
  );
  const [splitType, setSplitType] = useState(currentConfig.split_type);
  const [targetAmount, setTargetAmount] = useState(currentConfig.target_amount?.toString() || '');
  
  // Bank details for Razorpay (India)
  const [selectedCountry, setSelectedCountry] = useState('IN');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const currentCountry = COUNTRIES.find(c => c.code === selectedCountry) || COUNTRIES[0];
  const isIndiaSelected = selectedCountry === "IN";
  const currency = currentCountry.currency;
  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '₹';
  
  const perPersonAmount = targetAmount && participantCount > 0 
    ? (parseFloat(targetAmount) / participantCount).toFixed(2) 
    : null;
  
  const platformFee = currentConfig.platform_fee_per_participant || 10;
  
  // Check if payment is already configured (immutable once set)
  const isPaymentConfigured = !!(
    currentConfig.razorpay_linked_account_id || 
    currentConfig.stripe_connected_account_id ||
    (currentConfig.payment_enabled && currentConfig.target_amount)
  );

  const validateBankDetails = (): boolean => {
    if (!bankAccountNumber.trim()) {
      setPaymentError("Bank account number is required");
      return false;
    }
    if (!ifscCode.trim()) {
      setPaymentError("IFSC code is required");
      return false;
    }
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(ifscCode.toUpperCase())) {
      setPaymentError("Invalid IFSC code format (e.g., SBIN0001234)");
      return false;
    }
    if (!accountHolderName.trim()) {
      setPaymentError("Account holder name is required");
      return false;
    }
    setPaymentError(null);
    return true;
  };

  const handleSave = async () => {
    if (!targetAmount || parseFloat(targetAmount) <= 0) {
      toast.error("Please enter a valid target amount");
      return;
    }

    // For India (Razorpay), validate bank details
    if (isIndiaSelected) {
      if (!validateBankDetails()) {
        return;
      }
    }

    setSaving(true);
    try {
      const tableName = entityType === 'purchase' ? 'group_purchases' : 'gift_reminders';
      
      const updateData: Record<string, unknown> = {
        payment_type: paymentType,
        split_type: splitType,
        target_amount: parseFloat(targetAmount),
        currency: currency,
      };

      // Always enable payment when saving config
      if (entityType === 'event') {
        updateData.payment_enabled = true;
      }

      // Create Razorpay linked account for India
      if (isIndiaSelected) {
        if (paymentType === 'merchant' && beneficiaryName) {
          // Create linked account for beneficiary
          const { data: accountData, error: accountError } = await supabase.functions.invoke("create-razorpay-linked-account", {
            body: {
              recipientName: beneficiaryName.trim(),
              recipientType: "beneficiary",
              bankAccountNumber: bankAccountNumber.trim(),
              ifscCode: ifscCode.trim().toUpperCase(),
              accountHolderName: accountHolderName.trim(),
              organizerEmail: organizerEmail || '',
            }
          });
          
          if (accountError) throw accountError;
          if (!accountData?.success) throw new Error(accountData?.error || "Failed to create linked account");
          
          updateData.razorpay_linked_account_id = accountData.linkedAccountId;
          updateData.payment_provider = 'razorpay';
          updateData.platform_fee_per_participant = platformFee;
          
          if (entityType === 'purchase') {
            updateData.beneficiary_name = beneficiaryName.trim();
          } else {
            updateData.recipient_name = beneficiaryName.trim();
          }
        } else {
          // Organizer collects - create linked account for organizer
          const { data: accountData, error: accountError } = await supabase.functions.invoke("create-razorpay-linked-account", {
            body: {
              recipientName: accountHolderName.trim(),
              recipientType: "organizer",
              bankAccountNumber: bankAccountNumber.trim(),
              ifscCode: ifscCode.trim().toUpperCase(),
              accountHolderName: accountHolderName.trim(),
              organizerEmail: organizerEmail || '',
            }
          });
          
          if (accountError) throw accountError;
          if (!accountData?.success) throw new Error(accountData?.error || "Failed to create linked account");
          
          updateData.razorpay_linked_account_id = accountData.linkedAccountId;
          updateData.payment_provider = 'razorpay';
          updateData.platform_fee_per_participant = platformFee;
        }
      }

      const { error } = await supabase
        .from(tableName)
        .update(updateData)
        .eq('id', entityId);

      if (error) throw error;
      
      toast.success('Payment settings configured! This cannot be changed.');
      setIsEditing(false);
      onConfigUpdated();
    } catch (error: any) {
      console.error('Error saving payment config:', error);
      toast.error(error.message || 'Failed to save payment settings');
    } finally {
      setSaving(false);
    }
  };

  // Send invitation emails for Group Payment (only after payment is configured)
  const handleSendInvitations = async () => {
    if (entityType !== 'purchase') return;
    
    setSendingInvites(true);
    try {
      // Fetch participants who haven't been notified yet
      const { data: participants, error: fetchError } = await supabase
        .from("group_purchase_participants")
        .select("email, name")
        .eq("purchase_id", entityId)
        .is("notified_at", null)
        .neq("role", "organizer");
      
      if (fetchError) throw fetchError;
      
      if (!participants || participants.length === 0) {
        toast.info("All participants have already been notified");
        return;
      }

      // Send invitation emails
      const { error: emailError } = await supabase.functions.invoke("send-group-purchase-invitation", {
        body: {
          purchaseId: entityId,
          purchaseTitle: purchaseTitle || "Group Payment",
          organizerName: organizerName || organizerEmail?.split('@')[0] || 'Organizer',
          organizerEmail: organizerEmail,
          participants: participants.map(p => ({ email: p.email, name: p.name })),
          isPaymentGroup: true,
          targetAmount: currentConfig.target_amount,
          currency: currentConfig.currency,
          splitType: currentConfig.split_type,
        },
      });

      if (emailError) throw emailError;

      // Mark participants as notified
      await supabase
        .from("group_purchase_participants")
        .update({ notified_at: new Date().toISOString() })
        .eq("purchase_id", entityId)
        .is("notified_at", null)
        .neq("role", "organizer");

      toast.success(`Invitations sent to ${participants.length} participant(s)!`);
      onConfigUpdated();
    } catch (error: any) {
      console.error("Error sending invitations:", error);
      toast.error(error.message || "Failed to send invitations");
    } finally {
      setSendingInvites(false);
    }
  };

  // Non-organizers see nothing (they use the payment buttons in GroupPaymentSection)
  if (!isOrganizer) {
    return null;
  }

  // If already configured, show minimal summary for organizers
  if (isPaymentConfigured) {
    return (
      <div className="p-3 space-y-2">
        {/* Minimal status bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Lock className="w-4 h-4 text-emerald-600" />
            <span>Payment Ready</span>
            <span className="text-muted-foreground/60">•</span>
            <span>{currencySymbol}{currentConfig.target_amount?.toLocaleString()} target</span>
            <span className="text-muted-foreground/60">•</span>
            <span className="capitalize">{currentConfig.split_type} split</span>
          </div>
          {currentConfig.razorpay_linked_account_id && (
            <Badge variant="outline" className="text-xs gap-1 border-emerald-500/30 text-emerald-600">
              <IndianRupee className="w-3 h-3" />
              Auto-track
            </Badge>
          )}
          {currentConfig.stripe_connected_account_id && (
            <Badge variant="outline" className="text-xs gap-1 border-emerald-500/30 text-emerald-600">
              <CardIcon className="w-3 h-3" />
              Auto-track
            </Badge>
          )}
        </div>

        {/* Send Invitations - only for Group Payments with pending invites */}
        {entityType === 'purchase' && pendingInvitationsCount > 0 && (
          <div className="flex items-center justify-between gap-3 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
            <span className="text-sm text-amber-700 dark:text-amber-300">
              <Mail className="w-4 h-4 inline mr-1.5" />
              {pendingInvitationsCount} participant(s) waiting for invite
            </span>
            <Button
              size="sm"
              onClick={handleSendInvitations}
              disabled={sendingInvites}
              className="gap-1.5 h-7 text-xs"
            >
              {sendingInvites ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Send className="w-3 h-3" />
              )}
              Send
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Not editing yet - show the config prompt
  if (!isEditing) {
    return (
      <div className="space-y-3">
        <Card className="border-dashed hover:border-primary/50 transition-colors cursor-pointer" onClick={() => setIsEditing(true)}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-primary" />
                <CardTitle className="text-base">Configure Payments</CardTitle>
              </div>
              <Button variant="ghost" size="sm">
                <Settings2 className="w-4 h-4" />
              </Button>
            </div>
            <CardDescription>
              Set up payment collection. Once configured, settings cannot be changed.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Warning about pending invitations for Group Payments */}
        {entityType === 'purchase' && pendingInvitationsCount > 0 && (
          <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Important:</strong> {pendingInvitationsCount} participant(s) are waiting to be notified. 
              Configure payment settings first, then send invitations.
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  // Editing mode - show the form (same as AddGroupPaymentDialog)
  return (
    <Card className="border-primary/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-primary" />
            <CardTitle className="text-base">Payment Settings</CardTitle>
          </div>
          <Badge variant="destructive" className="gap-1">
            <Lock className="w-3 h-3" />
            One-time setup
          </Badge>
        </div>
        <CardDescription>
          ⚠️ These settings cannot be changed after saving.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Payment Type */}
        <div className="space-y-3">
          <Label>Where should payments go?</Label>
          <RadioGroup value={paymentType} onValueChange={(v) => setPaymentType(v as 'merchant' | 'organizer')}>
            <div className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="organizer" id="organizer" className="mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  <Label htmlFor="organizer" className="cursor-pointer font-medium">I'll collect payments</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Funds transfer directly to your bank account
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="merchant" id="merchant" className="mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Store className="w-4 h-4 text-primary" />
                  <Label htmlFor="merchant" className="cursor-pointer font-medium">Pay a beneficiary directly</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  E.g., vendor, shop, or service provider
                </p>
              </div>
            </div>
          </RadioGroup>

          {paymentType === "merchant" && (
            <div className="mt-3">
              <Label htmlFor="beneficiaryName" className="text-sm">Beneficiary Name</Label>
              <Input
                id="beneficiaryName"
                placeholder="e.g., ABC Restaurant, Travel Agency"
                value={beneficiaryName}
                onChange={(e) => setBeneficiaryName(e.target.value)}
                className="mt-1"
              />
            </div>
          )}
        </div>

        {/* Country Selection */}
        <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
          <Label className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            {paymentType === "organizer" ? "Your Country" : "Recipient's Country"}
          </Label>
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name} ({country.currency})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 text-sm">
            {isIndiaSelected ? (
              <>
                <IndianRupee className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground">
                  Payments via <strong className="text-foreground">Razorpay</strong> (UPI, Cards, Netbanking)
                </span>
              </>
            ) : (
              <>
                <CardIcon className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground">
                  Payments via <strong className="text-foreground">Stripe</strong> (Cards, Bank transfers)
                </span>
              </>
            )}
          </div>
        </div>

        {/* Bank Details for India (Razorpay) */}
        {isIndiaSelected && (
          <div className="space-y-3 p-3 rounded-lg border bg-primary/5">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <Building2 className="w-4 h-4" />
              Bank Account Details
              <span className="text-destructive">*</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Funds will be automatically transferred to this account. A ₹{platformFee} platform fee per participant applies.
            </p>
            
            <div className="space-y-1">
              <Label htmlFor="accountHolderName" className="text-sm flex items-center gap-1">
                Account Holder Name
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="accountHolderName"
                placeholder={paymentType === "organizer" ? "Your name as per bank" : "Beneficiary name as per bank"}
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e.target.value)}
              />
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="bankAccountNumber" className="text-sm flex items-center gap-1">
                Bank Account Number
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="bankAccountNumber"
                placeholder="Enter account number"
                value={bankAccountNumber}
                onChange={(e) => setBankAccountNumber(e.target.value)}
              />
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="ifscCode" className="text-sm flex items-center gap-1">
                IFSC Code
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ifscCode"
                placeholder="e.g., SBIN0001234"
                value={ifscCode}
                onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                maxLength={11}
              />
            </div>

            {paymentError && (
              <div className="flex items-center gap-1.5 text-destructive">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-xs">{paymentError}</span>
              </div>
            )}
          </div>
        )}

        {/* Stripe countries - coming soon */}
        {!isIndiaSelected && (
          <div className="p-3 rounded-lg bg-muted text-muted-foreground text-sm">
            <p className="font-medium text-foreground">Stripe Connect</p>
            <p className="text-xs mt-1">International payments via Stripe are coming soon.</p>
          </div>
        )}

        {/* Split Type */}
        <div className="space-y-3">
          <Label>How to split the cost?</Label>
          <RadioGroup value={splitType} onValueChange={(v) => setSplitType(v as 'equal' | 'custom' | 'open')}>
            <div className="grid grid-cols-3 gap-2">
              <div 
                className={`p-3 rounded-lg border text-center cursor-pointer transition-colors ${
                  splitType === 'equal' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setSplitType('equal')}
              >
                <Calculator className="w-5 h-5 mx-auto mb-1 text-primary" />
                <div className="text-sm font-medium">Equal Split</div>
                <div className="text-xs text-muted-foreground">Same amount each</div>
              </div>
              <div 
                className={`p-3 rounded-lg border text-center cursor-pointer transition-colors ${
                  splitType === 'custom' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setSplitType('custom')}
              >
                <Users className="w-5 h-5 mx-auto mb-1 text-primary" />
                <div className="text-sm font-medium">Custom</div>
                <div className="text-xs text-muted-foreground">Set per person</div>
              </div>
              <div 
                className={`p-3 rounded-lg border text-center cursor-pointer transition-colors ${
                  splitType === 'open' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setSplitType('open')}
              >
                <DollarSign className="w-5 h-5 mx-auto mb-1 text-primary" />
                <div className="text-sm font-medium">Open</div>
                <div className="text-xs text-muted-foreground">Any amount</div>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Target Amount */}
        <div className="space-y-2">
          <Label htmlFor="target-amount" className="flex items-center gap-1">
            Target Amount
            <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {currencySymbol}
            </span>
            <Input
              id="target-amount"
              type="number"
              placeholder="0.00"
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
              className="pl-8"
            />
          </div>
          {perPersonAmount && splitType === 'equal' && participantCount > 0 && (
            <p className="text-sm text-muted-foreground">
              ≈ {currencySymbol}{perPersonAmount} per person ({participantCount} participants)
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button 
            className="flex-1" 
            onClick={handleSave} 
            disabled={saving || (!isIndiaSelected)}
          >
            {saving ? 'Setting up...' : 'Configure & Lock'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
